import { DashboardService } from '@adgate/modules/main/services/dashboard-service';
import { StateService } from '@adgate/common/services/state-service';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogService } from '@adgate/common/services/dialog-service';
import { finalize, forkJoin } from 'rxjs';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { ModuleActivationService } from '@adgate/common/services/module-activation-service';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { BaseObject } from '@adgate/common/types/base-object.type';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { Commercial } from '@adgate/modules/tv/types/commecial.type';
import { Creativity } from '@adgate/modules/streaming/types/creativity.type';
import { ProductionRequest } from '@adgate/modules/production/types/production-request.type';
import { ProductionVersion } from '@adgate/modules/production/types/production-version.type';

interface DashboardStats {
  pending: number;
  rejected: number;
  review: number;
  approved: number;
}

interface DashboardEvent {
  objectType: number;
  event: string;
  hoursAgo: number;
}

interface UrgentItem {
  objectType: number;
  status: BaseObject;
  brand: string;
  title: string;
  hoursAgo: number;
}

@Component({
  selector: 'app-dashboard-component',
  imports: [
    CommonModule,
    ObjectDescPipe
  ],
  templateUrl: './dashboard-component.html',
  styleUrl: './dashboard-component.scss',
})
export class DashboardComponent implements OnInit
{
  public AdGateConstants = AdGateConstants;
  public stateService: StateService = inject(StateService);
  public moduleActivationService: ModuleActivationService = inject(ModuleActivationService);
  private dashboardService: DashboardService = inject(DashboardService);
  private dialogService: DialogService = inject(DialogService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  urgentItems: UrgentItem[] = [];
  activityLog: DashboardEvent[] = [];
  stats: DashboardStats = {
    pending: 0,
    rejected: 0,
    review: 0,
    approved: 0
  };

  ngOnInit(): void
  {
    this.onRefresh();
  }

  public onRefresh(): void
  {
    this.dialogService.showProgress();
    forkJoin([
      this.dashboardService.getStatsByStatus(null, null),
      this.dashboardService.getUrgentItems(null, null),
      this.dashboardService.getRecentActivity(null, null)
    ]).pipe(
      finalize(() => this.dialogService.hideProgress())
    ).subscribe({
      next: (r:RESTApplicationResponse[]) => {
        this.stats = r[0].data;
        this.urgentItems = r[1].data;
        this.activityLog = r[2].data;
        this.cdr.detectChanges();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });

    // Mock data - replace with actual service calls
    // this.activityLog = [
    //   { dot: 'var(--green-dot)', msg: 'AFP Plan Vital – Aprobado por el sistema', time: '2 hours ago' },
    //   { dot: 'var(--red-dot)', msg: 'Brochero Mundial – Rechazado: codec incompatible', time: '5 hours ago' },
    //   { dot: 'var(--purple-dot)', msg: 'Sacrifice 0105 – En revisión por JARV', time: '6 hours ago' },
    //   { dot: 'var(--yellow-dot)', msg: 'Champions Banco de Chile – Subido correctamente', time: '8 hours ago' },
    //   { dot: 'var(--green-dot)', msg: 'Infinia Diesel Premium – Transferido a DMED', time: '12 hours ago' },
    //   { dot: 'var(--yellow-dot)', msg: 'Sporting Champions – Esperando media', time: '1 day ago' }
    // ];
  }

  getGreeting(): string 
  {
    const currentHour = new Date().getHours();
    if (currentHour < 12) {
      return 'Good morning';
    } else if (currentHour < 18) {
      return 'Good afternoon';
    } else {
      return 'Good evening';
    }
  }

  getTimeFlag(hoursAgo: number): '24h' | '48h' | null 
  {
    if (hoursAgo >= 48) return '48h';
    if (hoursAgo >= 24) return '24h';
    return null;
  }

  getDotColor(objectType: number): string
  {
    switch(objectType) {
      case Commercial.OBJECT_TYPE: return 'var(--green-dot)';
      case Creativity.OBJECT_TYPE: return 'var(--yellow-dot)';
      case ProductionRequest.OBJECT_TYPE: return 'var(--purple-dot)';
      case ProductionVersion.OBJECT_TYPE: return 'var(--purple-dot)';
    }

    return 'var(--red-dot)';
  }

  getElapsedTime(hoursAgo: number): string 
  {
    if (hoursAgo < 1) return `< 1 hour ago`; 
    if (hoursAgo < 24) return `${hoursAgo} hours ago`; 
    if (hoursAgo < 48) return '1 day ago';
    if (hoursAgo >= 48) return `${Math.round(hoursAgo/24)} days ago`;

    return 'n/a';
  }

  navigateToPending(objectType: number): void 
  {
    switch(objectType) {
      case Commercial.OBJECT_TYPE:
        this.moduleActivationService.activateModule(AdGateConstants.MODULE_LINEAR);
        break;
      case Creativity.OBJECT_TYPE:
        this.moduleActivationService.activateModule(AdGateConstants.MODULE_STREAMING);
        break;
    }
  }
}
